import { Component } from "@theme/component";
import { CartAddEvent, CartErrorEvent } from "@theme/events";
import { cartPerformance } from "@theme/performance";
import { fetchConfig } from "@theme/utilities";

/**
 * Bundle subscription form wrapper used on bundle-builder pages.
 *
 * Why this exists:
 * - Our theme's declarative event system (`on:submit="/handleSubmit"`) only works when the
 *   element is an actual `Component` instance (see `assets/component.js`).
 * - If this element isn't defined, the browser falls back to a normal product form POST
 *   which navigates to `/cart` instead of opening the cart drawer.
 *
 * This component prevents the default submit navigation, adds the item via AJAX, then
 * dispatches `CartAddEvent` which `cart-drawer-component` listens for to auto-open.
 */
/**
 * @typedef {object} BundleSubscriptionFormRefs
 * @property {HTMLInputElement} variantId
 * @property {HTMLElement} liveRegion
 * @property {HTMLButtonElement | undefined} addToCartButton
 * @property {HTMLElement | undefined} addToCartTextError
 */

/**
 * @extends {Component<BundleSubscriptionFormRefs>}
 */
class BundleSubscriptionFormComponent extends Component {
  requiredRefs = ["variantId", "liveRegion"];

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  connectedCallback() {
    super.connectedCallback();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * Handles the submit event for the bundle product form.
   * @param {Event} event
   */
  handleSubmit(event) {
    const { addToCartTextError } = this.refs;
    event.preventDefault();

    if (this.#timeout) clearTimeout(this.#timeout);

    // Validate that required boxes are filled (bundle builder constraint)
    const bundleBuilder = /** @type {HTMLElement & {selections?: {box1?: unknown, box2?: unknown, box3?: unknown}} | null} */ (this.closest("bundle-builder-component"));
    if (bundleBuilder) {
      const selections = bundleBuilder.selections;
      const filledBoxes = [selections?.box1, selections?.box2, selections?.box3].filter(Boolean).length;

      if (filledBoxes < 3) {
        if (addToCartTextError) {
          addToCartTextError.classList.remove("hidden");
          addToCartTextError.textContent = "Please select all required boxes before adding to cart.";

          this.#timeout = window.setTimeout(() => {
            addToCartTextError.classList.add("hidden");
          }, 5000);
        }

        return;
      }
    }

    const form = this.querySelector("form");
    if (!form) throw new Error("Bundle subscription form: form element missing");

    const formData = new FormData(form);

    // Request cart sections so the drawer has fresh HTML to render
    const cartItemsComponents = document.querySelectorAll("cart-items-component");
    /** @type {string[]} */
    const sectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) sectionIds.push(item.dataset.sectionId);
    });
    formData.append("sections", sectionIds.join(","));

    const fetchCfg = fetchConfig("javascript", { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: "text/html",
      },
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          this.dispatchEvent(new CartErrorEvent(form.getAttribute("id") || "", response.message, response.description, response.errors));

          if (addToCartTextError) {
            addToCartTextError.classList.remove("hidden");

            // Reuse the text node if the user is spam-clicking
            const textNode = addToCartTextError.childNodes[2];
            if (textNode) {
              textNode.textContent = response.message;
            } else {
              addToCartTextError.appendChild(document.createTextNode(response.message));
            }
          }

          this.#setLiveRegionText(response.message);

          this.#timeout = window.setTimeout(() => {
            if (addToCartTextError) addToCartTextError.classList.add("hidden");
            this.#clearLiveRegionText();
          }, 10000);

          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: "bundle-subscription-form-component",
              itemCount: Number(formData.get("quantity")) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            })
          );

          return;
        }

        const id = formData.get("id");
        if (!id) throw new Error("Bundle subscription form: Form ID is required");

        if (addToCartTextError) {
          addToCartTextError.classList.add("hidden");
          addToCartTextError.removeAttribute("aria-live");
        }

        // Announce added state for screen readers
        const addedText = (Theme.translations && Theme.translations.added) || "Added to cart";
        this.#setLiveRegionText(addedText);
        window.setTimeout(() => this.#clearLiveRegionText(), 5000);

        this.dispatchEvent(
          new CartAddEvent({}, id.toString(), {
            source: "bundle-subscription-form-component",
            itemCount: Number(formData.get("quantity")) || Number(this.dataset.quantityDefault),
            productId: this.dataset.productId,
            sections: response.sections,
          })
        );
      })
      .catch((error) => {
        console.error("Error adding bundle (subscription wrapper) to cart:", error);
      })
      .finally(() => {
        cartPerformance.measureFromEvent("add:user-action", event);
      });
  }

  /** @param {string} text */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    if (liveRegion) liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    if (liveRegion) liveRegion.textContent = "";
  }
}

if (!customElements.get("bundle-subscription-form-component")) {
  customElements.define("bundle-subscription-form-component", BundleSubscriptionFormComponent);
}
